import { useState, useEffect } from "react";
import api from "../../api/client";

function emptyForm() {
  return { display_name: "", full_name: "", username: "", password: "", role: "", company_id: "", assigned_tl_id: "" };
}

export default function CreateAccountModal({ onClose, onRefresh }) {
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/roles").catch(() => ({ data: [] })),
      api.get("/companies/active").catch(() => ({ data: [] })),
      api.get("/team-leaders/active").catch(() => ({ data: [] })),
    ]).then(([rolesRes, companiesRes, tlRes]) => {
      setRoles(rolesRes.data);
      setCompanies(companiesRes.data);
      setTeamLeaders(tlRes.data);
      if (rolesRes.data.length > 0) setForm((f) => ({ ...f, role: rolesRes.data[rolesRes.data.length - 1].name }));
      if (companiesRes.data.length > 0) setForm((f) => ({ ...f, company_id: companiesRes.data[0].id }));
    });
  }, []);

  const isSalesAgentTL = form.role === "Sales Agent/TL";

  const handleChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Clear assigned_tl_id when role changes away from Sales Agent/TL
      if (key === "role" && value !== "Sales Agent/TL") {
        next.assigned_tl_id = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setApiError("");
  };

  const validate = () => {
    const errs = {};
    if (!form.display_name.trim()) errs.display_name = "Display name is required.";
    if (!form.username.trim()) errs.username = "Username is required.";
    if (!form.password) errs.password = "Password is required.";
    else if (form.password.length < 6) errs.password = "Password must be at least 6 characters.";
    if (!form.role) errs.role = "Role is required.";
    if (!form.company_id) errs.company_id = "Company is required.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    setApiError("");
    try {
      await api.post("/users", {
        display_name: form.display_name,
        full_name: form.full_name || "",
        username: form.username,
        password: form.password,
        role: form.role,
        company_id: form.company_id ? parseInt(form.company_id) : null,
        assigned_tl_id: isSalesAgentTL && form.assigned_tl_id ? parseInt(form.assigned_tl_id) : null,
      });
      onRefresh();
      onClose();
    } catch (err) {
      setApiError(err.response?.data?.detail || "Failed to create account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b bg-gray-50 rounded-t-xl flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Create Account</h2>
            <p className="text-xs text-gray-500 mt-0.5">Add a new user to the system</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none p-1">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {apiError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
              {apiError}
            </div>
          )}

          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
            <select
              value={form.company_id}
              onChange={(e) => handleChange("company_id", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${errors.company_id ? "border-red-400" : "border-gray-300"}`}
            >
              <option value="" disabled>Select a company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {errors.company_id && <p className="text-red-500 text-xs mt-1">{errors.company_id}</p>}
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => handleChange("display_name", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${errors.display_name ? "border-red-400" : "border-gray-300"}`}
              placeholder="e.g. Juan DC"
            />
            {errors.display_name && <p className="text-red-500 text-xs mt-1">{errors.display_name}</p>}
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => handleChange("full_name", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              placeholder="e.g. Juan Dela Cruz (optional)"
            />
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => handleChange("username", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${errors.username ? "border-red-400" : "border-gray-300"}`}
              placeholder="my-username"
            />
            {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => handleChange("password", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${errors.password ? "border-red-400" : "border-gray-300"}`}
              placeholder="Minimum 6 characters"
            />
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <select
              value={form.role}
              onChange={(e) => handleChange("role", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${errors.role ? "border-red-400" : "border-gray-300"}`}
            >
              <option value="" disabled>Select a role</option>
              {roles.map((r) => (
                <option key={r.id} value={r.name}>{r.name}</option>
              ))}
            </select>
            {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
          </div>

          {/* Assigned TL — only for Sales Agent/TL */}
          {isSalesAgentTL && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <label className="block text-sm font-medium text-amber-800 mb-1">Assigned Team Leader</label>
              <select
                value={form.assigned_tl_id}
                onChange={(e) => handleChange("assigned_tl_id", e.target.value)}
                className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">None</option>
                {teamLeaders.map((tl) => (
                  <option key={tl.id} value={tl.id}>{tl.name}</option>
                ))}
              </select>
              <p className="text-xs text-amber-600 mt-1">Optional. Select the Team Leader to assign to this Sales Agent/TL user.</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
            >
              {submitting ? "Creating…" : "Create Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
